
/**
 * Network monitoring utility
 * Tracks network health metrics and provides status information
 */

// Network status types
export enum NetworkStatus {
  GOOD = 'good',
  FAIR = 'fair',
  POOR = 'poor',
  UNKNOWN = 'unknown'
}

// Network metrics interface
export interface NetworkMetrics {
  latency: number;
  successRate: number;
  errorCount: number;
  lastChecked: Date | null;
}

// Default metrics
const defaultMetrics: NetworkMetrics = {
  latency: 0,
  successRate: 100,
  errorCount: 0,
  lastChecked: null
};

// In-memory storage for network metrics
let currentMetrics: NetworkMetrics = { ...defaultMetrics };
const metricsHistory: NetworkMetrics[] = [];

/**
 * Get network status based on metrics
 */
export const getNetworkStatus = (metrics: NetworkMetrics = currentMetrics): NetworkStatus => {
  if (!metrics.lastChecked) return NetworkStatus.UNKNOWN;
  
  // Determine status based on latency and success rate
  if (metrics.latency < 200 && metrics.successRate > 98) {
    return NetworkStatus.GOOD;
  } else if (metrics.latency < 500 && metrics.successRate > 90) {
    return NetworkStatus.FAIR;
  } else {
    return NetworkStatus.POOR;
  }
};

/**
 * Update network metrics with new values
 */
export const updateNetworkMetrics = (metrics: Partial<NetworkMetrics>): NetworkMetrics => {
  // Skip in production
  if (process.env.NODE_ENV === 'production') {
    return currentMetrics;
  }
  
  currentMetrics = {
    ...currentMetrics,
    ...metrics,
    lastChecked: new Date()
  };
  
  // Add to history (keep last 50)
  metricsHistory.push({ ...currentMetrics });
  if (metricsHistory.length > 50) {
    metricsHistory.shift();
  }
  
  return currentMetrics;
};

/**
 * Check network health by pinging a resource
 */
export const checkNetworkHealth = async (url: string = '/favicon.ico'): Promise<NetworkMetrics> => {
  // Skip in production
  if (process.env.NODE_ENV === 'production') {
    return { ...defaultMetrics, lastChecked: new Date() };
  }
  
  try {
    const startTime = performance.now();
    
    // Fetch with cache busting to ensure fresh response
    const response = await fetch(`${url}?_=${Date.now()}`, {
      method: 'HEAD',
      cache: 'no-cache'
    });
    
    const endTime = performance.now();
    const latency = Math.round(endTime - startTime);
    
    if (response.ok) {
      return updateNetworkMetrics({
        latency,
        successRate: currentMetrics.successRate * 0.9 + 100 * 0.1, // Weighted average
        errorCount: currentMetrics.errorCount
      });
    } else {
      return updateNetworkMetrics({
        latency,
        successRate: currentMetrics.successRate * 0.9 + 0 * 0.1, // Weighted average
        errorCount: currentMetrics.errorCount + 1
      });
    }
  } catch (error) {
    return updateNetworkMetrics({
      latency: 1000, // Assume high latency on error
      successRate: currentMetrics.successRate * 0.9, // Decrease by 10%
      errorCount: currentMetrics.errorCount + 1
    });
  }
};

/**
 * Get current network metrics
 */
export const getCurrentNetworkMetrics = (): NetworkMetrics => {
  return { ...currentMetrics };
};

/**
 * Get network metrics history
 */
export const getNetworkMetricsHistory = (limit: number = 50): NetworkMetrics[] => {
  // In production, return empty array
  if (process.env.NODE_ENV === 'production') {
    return [];
  }
  return metricsHistory.slice(-limit);
};

/**
 * Reset network metrics
 */
export const resetNetworkMetrics = (): void => {
  currentMetrics = { ...defaultMetrics };
  metricsHistory.length = 0;
};

export default {
  checkNetworkHealth,
  getCurrentNetworkMetrics,
  getNetworkMetricsHistory,
  getNetworkStatus,
  NetworkStatus
};
